// src/features/dashboard/pages/EmployeeIdleTimeReport.tsx

import {
  useEffect,
  useRef,
} from 'react';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import EmployeeIdleHeader from '../../components/EmployeeIdleTime/EmployeeIdleHeader';

import EmployeeIdleKPICards from '../../components/EmployeeIdleTime/EmployeeIdleKPICards';

import IdleMinutesChart from '../../components/EmployeeIdleTime/IdleMinutesChart';

import WTDIdleTrendChart from '../../components/EmployeeIdleTime/WTDIdleTrendChart';

import TeamIdleComparisonChart from '../../components/EmployeeIdleTime/TeamIdleComparisonChart';

import IdleDailyTable from '../../components/EmployeeIdleTime/IdleDailyTable';

import {
  useEmployeeIdleTime,
} from '../../hooks/Employee/useEmployeeIdleTime';


/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 *
 * When browser zoom changes:
 *
 *   80%  -> 100%
 *   100% -> 110%
 *   110% -> 125%
 *   125% -> 100%
 *
 * Chrome changes window.devicePixelRatio.
 *
 * We detect that change and automatically reload the page.
 *
 * IMPORTANT:
 *
 * - Does NOT change API logic.
 * - Does NOT change date filter logic.
 * - Does NOT change chart logic.
 * - Does NOT change responsive CSS.
 * - Page reload happens ONLY when browser zoom changes.
 * ========================================================================== */

const useBrowserZoomAutoRefresh = () => {

  const initialDevicePixelRatio =
    useRef<number>(
      window.devicePixelRatio,
    );

  const refreshTriggered =
    useRef<boolean>(
      false,
    );


  useEffect(() => {

    /*
     * Save the devicePixelRatio when the page is loaded.
     *
     * Example:
     *
     * 80% zoom
     * devicePixelRatio = 0.8
     *
     * After changing zoom:
     *
     * 125% zoom
     * devicePixelRatio = 1.25
     */

    initialDevicePixelRatio.current =
      window.devicePixelRatio;


    /* ========================================================================
     * REFRESH PAGE
     * ====================================================================== */

    const refreshPage =
      () => {

        /*
         * Prevent multiple reload calls while Chrome is changing zoom.
         */

        if (
          refreshTriggered.current
        ) {

          return;
        }


        refreshTriggered.current =
          true;


        console.log(
          '==============================================',
        );

        console.log(
          'BROWSER ZOOM CHANGED',
        );

        console.log(
          'Previous devicePixelRatio:',
          initialDevicePixelRatio.current,
        );

        console.log(
          'Current devicePixelRatio:',
          window.devicePixelRatio,
        );

        console.log(
          'Automatically refreshing page...',
        );

        console.log(
          '==============================================',
        );


        /*
         * Give Chrome a small amount of time to finish applying
         * the new zoom before reloading.
         */

        window.setTimeout(
          () => {

            window.location.reload();

          },
          100,
        );
      };


    /* ========================================================================
     * CHECK ZOOM
     * ====================================================================== */

    const checkZoom =
      () => {

        if (
          refreshTriggered.current
        ) {

          return;
        }


        const currentDevicePixelRatio =
          window.devicePixelRatio;


        const previousDevicePixelRatio =
          initialDevicePixelRatio.current;


        /*
         * Browser zoom changed.
         */

        if (
          currentDevicePixelRatio !==
          previousDevicePixelRatio
        ) {

          refreshPage();

        }

      };


    /* ========================================================================
     * POLLING
     *
     * This is the main fix.
     *
     * Chrome does not always fire the resize event consistently when
     * browser zoom changes.
     *
     * Therefore we check devicePixelRatio every 200 milliseconds.
     *
     * This DOES NOT reload the page every 200ms.
     *
     * It reloads ONLY when the devicePixelRatio is different.
     * ====================================================================== */

    const zoomInterval =
      window.setInterval(
        checkZoom,
        200,
      );


    /* ========================================================================
     * RESIZE EVENT
     *
     * Some Chrome versions fire resize when zoom changes.
     *
     * We check immediately when that happens.
     * ====================================================================== */

    const handleResize =
      () => {

        checkZoom();

      };


    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );


    /* ========================================================================
     * VISUAL VIEWPORT
     *
     * Chrome/Edge can expose browser viewport changes here.
     * ====================================================================== */

    const visualViewport =
      window.visualViewport;


    if (
      visualViewport
    ) {

      visualViewport.addEventListener(
        'resize',
        handleResize,
      );
    }


    /* ========================================================================
     * CLEANUP
     * ====================================================================== */

    return () => {

      window.clearInterval(
        zoomInterval,
      );


      window.removeEventListener(
        'resize',
        handleResize,
      );


      if (
        visualViewport
      ) {

        visualViewport.removeEventListener(
          'resize',
          handleResize,
        );
      }

    };

  }, []);
};


/* ============================================================================
 * LOADER
 * ========================================================================== */

const EmployeeIdleTimeLoader = () => {

  return (

    <div
      className="
        flex
        min-h-[320px]
        w-full
        min-w-0
        items-center
        justify-center
        rounded-[12px]
        bg-white

        px-4
        py-8

        sm:min-h-[420px]

        lg:min-h-[500px]

        xl:min-h-[560px]
      "
    >

      {/* ====================================================================
          LOADING CONTENT
      ==================================================================== */}

      <div
        className="
          flex
          flex-col
          items-center
          justify-center
        "
      >

        {/* ==================================================================
            SPINNER
        ================================================================== */}

        <div
          className="
            h-[36px]
            w-[36px]
            animate-spin
            rounded-full
            border-[4px]
            border-[#E4E7EC]
            border-t-[#2563EB]

            sm:h-[40px]
            sm:w-[40px]
          "
        />


        {/* ==================================================================
            LOADING TEXT
        ================================================================== */}

        <p
          className="
            mt-[12px]
            text-center
            text-[12px]
            font-medium
            text-[#475467]

            sm:text-[13px]
          "
        >
          Loading Idle Time...
        </p>


        {/* ==================================================================
            SUB TEXT
        ================================================================== */}

        <p
          className="
            mt-[4px]
            text-center
            text-[10px]
            text-[#98A2B3]

            sm:text-[11px]
          "
        >
          Fetching your latest activity data
        </p>

      </div>

    </div>
  );
};


/* ============================================================================
 * CONTENT
 * ========================================================================== */

const EmployeeIdleTimeContent = () => {

  const {
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================================
     API DATA
     ========================================================================== */

  const {
    loading,
    kpis,
    hourlyIdle,
    wtdTrend,
    teamComparison,
    dailyDetails,
  } =
    useEmployeeIdleTime();


  /* ==========================================================================
     GLOBAL LOADER
     ========================================================================== */

  useEffect(() => {

    if (
      !loading
    ) {

      const timer =
        window.setTimeout(
          () => {

            hideLoader();

          },
          300,
        );


      return () => {

        window.clearTimeout(
          timer,
        );

      };

    }


    return undefined;

  }, [
    loading,
    hideLoader,
  ]);


  /* ==========================================================================
     PAGE
     ========================================================================== */

  return (

    <div
      className="
        flex
        min-h-screen
        w-full
        max-w-none
        min-w-0
        flex-col
        overflow-x-hidden
        bg-[#F4F5F7]

        px-2
        pb-2

        sm:px-3
        sm:pb-3

        md:px-4
        md:pb-4

        lg:px-5
        lg:pb-5
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
      ====================================================================== */}

      <div
        className="
          w-full
          max-w-none
          min-w-0
          self-stretch
        "
      >

        <DashboardHeader
          showBackButton
          breadcrumbs={[
            {
              label:
                'Work Vista',

              path:
                '/',
            },

            {
              label:
                'My Dashboard',

              path:
                '/employee-dashboard',
            },

            {
              label:
                'Idle Time',
            },
          ]}
        />

      </div>


      {/* ======================================================================
          MAIN CONTENT
      ====================================================================== */}

      <main
        className="
          -mt-[14px]

          block

          w-full
          max-w-none
          min-w-0

          flex-1

          self-stretch

          overflow-visible

          rounded-[16px]

          border
          border-[#D0D5DD]

          bg-[#F8F9FB]

          p-3

          sm:p-4

          md:p-4

          lg:p-5
        "
      >

        {/* ====================================================================
            LOADING
        ==================================================================== */}

        {loading ? (

          <div
            className="
              w-full
              min-w-0
            "
          >

            <EmployeeIdleTimeLoader />

          </div>

        ) : (

          <div
            className="
              flex
              w-full
              min-w-0
              flex-col
            "
          >

            {/* ================================================================
                EMPLOYEE IDLE HEADER
            ================================================================ */}

            <section
              className="
                block
                w-full
                max-w-none
                min-w-0
              "
            >

              <EmployeeIdleHeader
                kpi={
                  kpis
                }
              />

            </section>


            {/* ================================================================
                KPI CARDS
            ================================================================ */}

            <section
              className="
                mt-4

                block

                w-full
                max-w-none
                min-w-0

                sm:mt-5
              "
            >

              <div
                className="
                  block
                  w-full
                  max-w-none
                  min-w-0
                "
              >

                <EmployeeIdleKPICards
                  data={
                    kpis
                  }
                />

              </div>

            </section>


            {/* ================================================================
                IDLE MINUTES BY HOUR
            ================================================================ */}

            <section
              className="
                mt-4

                block

                w-full
                max-w-none
                min-w-0

                sm:mt-5
              "
            >

              <div
                className="
                  block
                  w-full
                  max-w-none
                  min-w-0
                  overflow-hidden
                "
              >

                <IdleMinutesChart
                  data={
                    hourlyIdle
                  }

                  target={
                    90
                  }

                  loading={
                    false
                  }
                />

              </div>

            </section>


            {/* ================================================================
                WTD + TEAM COMPARISON

                These two charts remain side-by-side on desktop.

                min-w-0 is important for browser zoom/responsive behavior.
            ================================================================ */}

            <section
              className="
                mt-4

                grid

                w-full
                max-w-none
                min-w-0

                grid-cols-1

                items-stretch

                gap-4

                sm:mt-5
                sm:gap-5

                lg:grid-cols-2
                lg:gap-5

                xl:gap-6
              "
            >

              {/* ==============================================================
                  WTD IDLE TREND
              ============================================================== */}

              <div
                className="
                  flex
                  min-w-0
                  w-full
                  max-w-none
                  flex-col
                "
              >

                <div
                  className="
                    min-w-0
                    w-full
                    max-w-none
                    overflow-hidden
                  "
                >

                  <WTDIdleTrendChart
                    data={
                      wtdTrend
                    }

                    loading={
                      false
                    }
                  />

                </div>

              </div>


              {/* ==============================================================
                  TEAM IDLE COMPARISON
              ============================================================== */}

              <div
                className="
                  flex
                  min-w-0
                  w-full
                  max-w-none
                  flex-col
                "
              >

                <div
                  className="
                    min-w-0
                    w-full
                    max-w-none
                    overflow-hidden
                  "
                >

                  <TeamIdleComparisonChart
                    data={
                      teamComparison
                    }

                    loading={
                      false
                    }
                  />

                </div>

              </div>

            </section>


            {/* ================================================================
                DAILY DETAILS
            ================================================================ */}

            <section
              className="
                mt-4

                block

                w-full
                max-w-none
                min-w-0

                sm:mt-5
              "
            >

              <div
                className="
                  block
                  w-full
                  max-w-none
                  min-w-0

                  overflow-x-auto
                  overflow-y-hidden
                "
              >

                <IdleDailyTable
                  data={
                    dailyDetails
                  }

                  loading={
                    false
                  }
                />

              </div>

            </section>

          </div>

        )}

      </main>

    </div>
  );
};


/* ============================================================================
 * PAGE
 * ========================================================================== */

const EmployeeIdleTimeReport = () => {

  /*
   * IMPORTANT:
   *
   * Enable automatic browser zoom refresh.
   *
   * This does not create another date provider.
   *
   * Date filter remains controlled by the existing DashboardDateProvider
   * at the common Employee Dashboard/layout level.
   */

  useBrowserZoomAutoRefresh();


  return (
    <EmployeeIdleTimeContent />
  );
};


export default EmployeeIdleTimeReport;